/**
 * Main-thread API for the persistent engine worker.
 *
 * Compiled with tsconfig.json (lib: ES2022 + DOM) so that the `Worker` and
 * `AbortSignal` browser APIs are available.
 *
 * Usage with Vite:
 *
 *   import EngineWorker from '@procsim/engine-worker/worker?worker';
 *   const client = createEngineWorkerClient(new EngineWorker());
 *
 * Usage with a URL (e.g. in tests or non-Vite environments):
 *
 *   const url = new URL('@procsim/engine-worker/worker', import.meta.url);
 *   const client = createEngineWorkerClient(url);
 */
// ── Factory ───────────────────────────────────────────────────────────────────
/**
 * Wrap an existing `Worker` instance (or construct one from a URL) into an
 * `EngineWorkerClient`.
 *
 * Accepting a `Worker | string | URL` lets the caller decide how to bundle
 * the worker script.  Vite users pass `new EngineWorker()` (imported with
 * `?worker`); other bundlers or test environments can pass a URL string.
 */
export function createEngineWorkerClient(workerOrUrl, options = {}) {
    const worker = workerOrUrl instanceof Worker ? workerOrUrl : new Worker(workerOrUrl, { type: 'module' });
    const pending = new Map();
    let nextId = 0;
    let dead = false;
    // ── Incoming messages from the worker ──────────────────────────────────────
    worker.addEventListener('message', (evt) => {
        const msg = evt.data;
        const job = pending.get(msg.id);
        if (!job)
            return;
        if (msg.type === 'progress') {
            job.onProgress?.(msg.pct);
            return; // job remains pending until result or error
        }
        pending.delete(msg.id);
        if (msg.type === 'schedule_result') {
            job.resolve(msg.outcome);
        }
        else if (msg.type === 'simulate_result') {
            job.resolve(msg.result);
        }
        else if (msg.type === 'chance_crash_result') {
            job.resolve(msg.plan);
        }
        else if (msg.type === 'simulate_shard_result') {
            job.resolve(msg.raw);
        }
        else if (msg.type === 'error') {
            job.reject(new Error(msg.message));
        }
    });
    worker.addEventListener('error', (evt) => {
        if (dead)
            return; // already handled (e.g. dispose() fired this)
        dead = true;
        const message = `EngineWorker unhandled error: ${evt.message}`;
        for (const job of pending.values()) {
            job.reject(new Error(message));
        }
        pending.clear();
        // Phase 50 Slice 8 / audit C-7 — terminate the dead worker so a
        // future caller can't accidentally postMessage to it (which would
        // silently never resolve). The owning singleton / pool nulls out
        // its reference via `onWorkerError` so the next access lazily
        // spawns a fresh client.
        try {
            worker.terminate();
        }
        catch {
            // Best-effort — some environments throw if terminate is called
            // on an already-terminated worker. Either way, we're done with it.
        }
        if (options.onWorkerError) {
            // Wrap in try/catch so a buggy callback can't prevent the death
            // state from being set or future calls from being rejected.
            try {
                options.onWorkerError(evt);
            }
            catch (err) {
                console.error('EngineWorker onWorkerError handler threw:', err);
            }
        }
    });
    function allocateId() {
        return String(nextId++);
    }
    /** Reject a fresh call when the worker is already dead. */
    function rejectIfDead() {
        if (!dead)
            return null;
        return Promise.reject(new Error('EngineWorkerClient is dead — spawn a fresh client via createEngineWorkerClient'));
    }
    function registerAbort(id, signal, reject) {
        signal.addEventListener('abort', () => {
            if (!pending.has(id))
                return; // already resolved
            pending.delete(id);
            const req = { type: 'cancel', id };
            worker.postMessage(req);
            reject(signal.reason);
        });
    }
    // ── Public API ────────────────────────────────────────────────────────────
    return {
        scheduleAsync(input, signal) {
            const deadReject = rejectIfDead();
            if (deadReject)
                return deadReject;
            const id = allocateId();
            return new Promise((resolve, reject) => {
                pending.set(id, {
                    resolve: resolve,
                    reject,
                });
                if (signal)
                    registerAbort(id, signal, reject);
                const req = { type: 'schedule', id, input };
                worker.postMessage(req);
            });
        },
        simulateAsync(input, signal, onProgress) {
            const deadReject = rejectIfDead();
            if (deadReject)
                return deadReject;
            const id = allocateId();
            return new Promise((resolve, reject) => {
                pending.set(id, {
                    resolve: resolve,
                    reject,
                    ...(onProgress !== undefined ? { onProgress } : {}),
                });
                if (signal)
                    registerAbort(id, signal, reject);
                const req = { type: 'simulate', id, input };
                worker.postMessage(req);
            });
        },
        chanceCrashAsync(input, deadline, opts, signal, onProgress) {
            const deadReject = rejectIfDead();
            if (deadReject)
                return deadReject;
            const id = allocateId();
            return new Promise((resolve, reject) => {
                pending.set(id, {
                    resolve: resolve,
                    reject,
                    ...(onProgress !== undefined ? { onProgress } : {}),
                });
                if (signal)
                    registerAbort(id, signal, reject);
                const req = {
                    type: 'chance_crash',
                    id,
                    input,
                    deadline,
                    opts,
                };
                worker.postMessage(req);
            });
        },
        simulateShardAsync(input, opts, signal) {
            const deadReject = rejectIfDead();
            if (deadReject)
                return deadReject;
            const id = allocateId();
            return new Promise((resolve, reject) => {
                pending.set(id, {
                    resolve: resolve,
                    reject,
                });
                if (signal)
                    registerAbort(id, signal, reject);
                const req = {
                    type: 'simulate_shard',
                    id,
                    input,
                    iterStart: opts.iterStart,
                    iterEnd: opts.iterEnd,
                    totalIterations: opts.totalIterations,
                };
                worker.postMessage(req);
            });
        },
        dispose() {
            if (dead)
                return; // idempotent — already done by an earlier error event
            dead = true;
            try {
                worker.terminate();
            }
            catch {
                // ignore
            }
            const err = new Error('EngineWorkerClient disposed');
            for (const job of pending.values()) {
                job.reject(err);
            }
            pending.clear();
        },
        isDead() {
            return dead;
        },
    };
}
//# sourceMappingURL=index.js.map